/**
 * Button component - Primary action button throughout the app.
 * NOW THEME-AWARE
 */

import React, { useMemo } from 'react';
import {
  TouchableOpacity,
  Text,
  StyleSheet,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

export function Button({
  title,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  style,
  textStyle,
}: ButtonProps) {
  const { theme } = useTheme();

  // Create themed styles
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const getButtonStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = styles.button;

    if (variant === 'secondary') {
      return {
        ...baseStyle,
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.border,
      };
    }
    if (variant === 'danger') {
      return { ...baseStyle, backgroundColor: theme.colors.error, borderColor: theme.colors.error };
    }
    return {
      ...baseStyle,
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    };
  };

  const getTextStyle = (): TextStyle => {
    const baseStyle: TextStyle = styles.text;

    if (variant === 'secondary') {
      return { ...baseStyle, color: theme.colors.text };
    }
    if (variant === 'danger') {
      return { ...baseStyle, color: theme.colors.textInverse };
    }
    return { ...baseStyle, color: theme.colors.textInverse };
  };

  return (
    <TouchableOpacity
      style={[getButtonStyle(), disabled && styles.disabled, style]}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.7}
    >
      {loading ? (
        <ActivityIndicator color={theme.colors.textInverse} />
      ) : (
        <Text style={[getTextStyle(), textStyle]}>{title}</Text>
      )}
    </TouchableOpacity>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    button: {
      flex: 1,
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderWidth: 2,
    },
    text: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    disabled: {
      opacity: 0.5,
    },
  });
}
